import React, { useEffect, useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Route, BrowserRouter as Router, Switch, useLocation } from 'react-router-dom';
import i18n from 'utils/I18n';

import AuthRequiredView from 'component/common/AuthRequiredView';
import CookieBar from 'component/common/CookieBar';
import { CustomToast } from 'component/common/CustomToast';
import Footer from 'component/common/Footer';
import OpenAppBanner from 'component/common/OpenAppBanner';
import ScrollToTop from 'component/common/ScrollToTop';
import Navigator from 'component/common/navigator/Navigator';
import NotFoundPage from 'component/error/NotFound';
import MaintenanceOverlay from 'container/MaintenanceOverlay'

import { createAction } from 'utils';
import { SHOW_NAVIGATOR, APP_NAME } from 'utils/Config';
import { onlyShowWhiteListPage, NO_WEBAPP_WHITE_PAGE_LIST } from 'utils/UIConfigUtils'
import { inMiniProgram, updateRouteTitle } from 'utils/WeChatUtil';

import './App.scss';
import BottomNavigator from 'component/common/navigator/BottomNavigator';
import { BASIC_WEBAPP_ROUTES, GIFT_CARD_ROUTE } from 'utils/RouteUtil'
import { mobileDetectIsInMainNav } from 'component/common/navigator/NavigatorUtils'

function AppContent() {
  const { language } = useSelector((state) => ({
    language: state.users.language,
  }));
  const dispatch = useDispatch();
  const location = useLocation();
  const isInMiniProgram = inMiniProgram();

  useEffect(() => {
    document.documentElement.lang = language;
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language]);

  useEffect(() => {
    if (isInMiniProgram) {
      document
        .getElementById('root')
        .style.setProperty('--wechat-safe-area', '34px');
    }
    if (!SHOW_NAVIGATOR || isInMiniProgram) {
      document
        .getElementById('root')
        .style.setProperty('--page-nav-height', '0px');
    }
    dispatch({ type: "enforceApp/getMaintenanceInfo" })
  }, []);

  useEffect(() => {
    window.document.title = APP_NAME
    if (window.self !== window.top) {
      dispatch(
        createAction('users/updateState')({
          isOpenInOtherSite: true,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateRouteTitle({ pathname: location.pathname })
    const isInMainNav = mobileDetectIsInMainNav(location.pathname)
    const totalBottomHeight = isInMainNav ? 'var(--default-page-bottom-nav-total-height)' : '0px'
    document
        .getElementById('root')
        .style.setProperty('--page-bottom-nav-total-height', totalBottomHeight);
        
    if (isInMiniProgram) {
      dispatch({ type: "weChat/setUpWeChatConfig", payload: { onFailed: () => {  } } })
      dispatch({ type: "users/updateState", payload: { showOpenAppBanner: false } })
    }
    dispatch({ type: "enforceApp/getMaintenanceInfo" })
  }, [location.pathname])

  const finalRoutes = useMemo(() => {
    const showWhiteListPage = onlyShowWhiteListPage()
    if (showWhiteListPage) {
      return BASIC_WEBAPP_ROUTES.filter((route) => NO_WEBAPP_WHITE_PAGE_LIST.includes(route.path))
    }
    return BASIC_WEBAPP_ROUTES
  }, [])

  const hideNavigator =
    window.location.href?.indexOf(GIFT_CARD_ROUTE.path) !== -1;

  const isTransactionHistoryDetailPage =
    window.location.href?.indexOf('/transaction_history_detail') !== -1;
  const g6Background = isTransactionHistoryDetailPage;

  const displayPageTopNavigator = SHOW_NAVIGATOR && !isInMiniProgram

  const noNavigatorPage = () => {
    return (
      <Switch>
        <Route path={GIFT_CARD_ROUTE.path} component={GIFT_CARD_ROUTE.component} />
      </Switch>
    );
  };

  return (
    <>
      <CustomToast />
      <ScrollToTop>
        <div className="app_container">
          {hideNavigator ? (
            noNavigatorPage()
          ) : (
            <>
              <OpenAppBanner />
              <div
                className={`app_navigator_container ${g6Background ? 'g6_background' : ''
                    } ${displayPageTopNavigator ? '' : 'hidden'}`
                }
              >
                <Navigator />
              </div>
              <Container className={`page_container`}>
              {/* A <Switch> looks through its children <Route>s and
              renders the first one that matches the current URL. */}
                <Switch>
                  {finalRoutes?.map((item) => (
                    <Route
                      path={item?.path}
                      exact={item?.exact}
                      {...(item?.isAuthProtected
                        ? {
                          render: (props) => (
                            <AuthRequiredView 
                            >
                              <item.component {...props} />
                            </AuthRequiredView>
                          ),
                        }
                        : { component: item.component })}
                    />
                  ))}
                  <Route component={NotFoundPage} />
                  </Switch>
                </Container>
                <Footer />
                <CookieBar />
                <BottomNavigator />
            </>
          )}
        </div>
      </ScrollToTop>
      <MaintenanceOverlay />
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  )
}

export default App;
